import Config from '../config';
import $ from '../../utils/net';
import { msg } from "@lingui/core/macro";
import { CasinoApi, GameEntry, GamesBrand } from '../casino-feed.js';
import { makeReactive } from '../../utils/reactive-store';

function indexBy<T>(tag: string) {
    return (c: { [key: string]: T; }, data: T) => ({ ...c, [data[tag]]: data });
}

function safe<T>(fn: (() => T), err: null | T): null | T { try { return fn(); } catch (e) { return err; } }

const indexByCategory = (providers: CasinoServiceState["providers"]) => {
    const providersList = Object.values(providers);
    return providersList.reduce((c, P: GamesBrand) => {
        P.sub.forEach(S => {
            return S.tags.filter(tag => tag != S.id).forEach(T => {
                return c[T] = (c[T] || []).concat(S);
            });
        })
        return c;
    }, {} as { [key: string]: GameEntry[] })
};

const adjustCategories = (categories: { [key: string]: GameEntry[] }) => {
    return Object.entries(categories).map(([k, C]) => ({
        id: k,
        name: k.replace(/[-_]/g, ' ').split(' ').map(S => S.substr(0, 1).toUpperCase() + S.substr(1)).join(' '),
        sub: C.sort((a, b) => a.sort - b.sort || a.id.localeCompare(b.id))
    })).reduce(indexBy('id'), {});
};

const flattenSlotList = (state: CasinoServiceState) => {
    return Array.from(new Set(Object.values(state.slots || {})
        .flatMap((T: GamesBrand) => T.sub.filter(S => !state.filter.category || S.category == state.filter.category))
    ));
}

export interface CasinoServiceData {
    providers: { [key: string]: GamesBrand },
    'providers-timestamp': number,
}

export interface CasinoServiceCache {
    slots: false | { [key: string]: GamesBrand },
    cache: false,
}

export interface CasinoServiceFilters {
    filter: {
        provider: false | string,
        category: false | string,
    },
}

export interface CasinoServiceState extends CasinoServiceData, CasinoServiceCache, CasinoServiceFilters {
}

const state: CasinoServiceState = makeReactive({
    providers: {},
    'providers-timestamp': 0,
    slots: false,
    filter: {
        provider: false,
        category: false
    },
    cache: false
});

export class CasinoService implements CasinoApi {
    eventListeners: (() => any)[] = [];

    async setup(): Promise<void> {
        const brands = await this.getGamesDB();
        state.providers = brands;
        state['providers-timestamp'] = Date.now();
        state.slots = this.providersToCategories(brands);
        this.eventListeners.forEach(cb => cb());
    }
    applyCategoryFilter(category: false | string, provider: false | string = false) {
        state.filter.category = category;
        state.filter.provider = provider;
        this.eventListeners.forEach(cb => cb());
    }
    getSelectedCategoryId() {
        return state.filter.category;
    }
    getSelectedCategoryName() {
        return !!state.filter.category && !!state.providers[state.filter.category] ?
            state.providers[state.filter.category].name : window.Translation(msg`Tutte le Slots`);
    }
    getSelectedCategoryLogo() {
        console.info(state.providers);
        return !!state.filter.category && !!state.providers[state.filter.category] ?
            state.providers[state.filter.category].logo : null;
    }
    clearCategoryFilter() {
        this.applyCategoryFilter(false);
    }
    async getGamesDB(): Promise<CasinoServiceState["providers"]> {
        const cacheContents = safe(() => JSON.parse(window.safeLocalStorage.getItem('full-providers-db')), false);
        const lastFetchTime = parseInt(window.safeLocalStorage.getItem('full-providers-db-timestamp') || '0');
        if (Date.now() - lastFetchTime <= 5 * 60e3 && !!cacheContents) {
            return cacheContents;
        }
        const fetchPromise = new Promise((res, rej) => $.getJSON(Config.softionAPI + "/ccg/games?json", (err, data) => { return !!err ? rej(err) : res(data) }))
        const gamesResponse = await fetchPromise as any;
        const brands: CasinoServiceState["providers"] = {};
        gamesResponse.buttons.forEach(button => {
            brands[button.category] = brands[button.category] || {
                id: button.category,
                provider: gamesResponse.parent,
                name: button.name,
                logo: button.icon,
                //resources: [],
                sub: []
            };
            //brands[button.category].resources.push(button.url);
        });

        for (var group in gamesResponse.groups) {
            const games = gamesResponse.groups[group];
            const knownIds = {};
            for (var game of games) {
                // Bug nel CCG alcuni giochi arrivano doppiati e i tag sono sparsi
                if (!!knownIds[game.gameId]) {
                    const realGame = brands[group].sub[knownIds[game.gameId]];
                    const unifiedTags = new Set(realGame.tags.concat(game.tag.split(',')));
                    realGame.tags = [...unifiedTags]
                    continue;
                }
                knownIds[game.gameId] = brands[group].sub.length;
                const gameEntry: GameEntry = {
                    id: game.gameId,
                    name: game.label,
                    category: group,
                    provider: gamesResponse.parent,
                    logo: game.logoUrl,
                    tags: game.tag.split(','),
                    sort: 0
                };
                // GameEntry
                brands[group].sub.push(gameEntry);
            }
        }

        try {
            window.safeLocalStorage.setItem('full-providers-db', JSON.stringify(brands))
            window.safeLocalStorage.setItem('full-providers-db-timestamp', "" + Date.now())
        } catch (e) { }

        return brands;
    }

    providersToCategories(providers: CasinoServiceState["providers"]) {
        return adjustCategories(indexByCategory(providers));
    }

    providersList() {
        return state.providers;
    }

    getPlayUrl(provider: string, brand: string, game: string) {
        return Config.softionAPI + '/ccg/play/' + [provider, brand, game].join('/');
    }

    listenChanges = (cb: { (): void;}) => {
        return this.eventListeners.push(cb);
    }
    unlistenChanges = (cb: { (): void;}) => {
        return this.eventListeners = this.eventListeners.filter(c => c != cb);
    }
    flattenSlotList = () => {
        const f = flattenSlotList(state); return f;
    }
    notifyChanges = () => this.eventListeners.forEach(cb => cb());
}
